import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  List,
  ListItemText,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  ListItemButton,
  styled,
  Badge,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import checkIcon from "../../../assets/images/Icons/ic_check.svg";
import errorIcon from "../../../assets/images/Icons/ic_error.svg";
import { CustomeDialog } from "../../../shared";
import DeleteBatchesDialog from "./DeleteBatchesDialog";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CircularProgress from "@mui/material/CircularProgress";
import { formatWeightWithUnits } from "../../../utils";
import arrowIcon from "../../../assets/images/Icons/Vector (9).svg";
import CustomDialogFooter from "../../../shared/components/CustomDialogFooter";

const StyledBadge = styled(Badge)(({ active }) => ({
  "& .MuiBadge-badge": {
    right: 4,
    top: 10,
    padding: "0 4px",
    position: "relative",
    background: active ? "#FFFFFF" : "#263141",
    color: active ? "#263141" : "#FFFFFF",
  },
}));

const ProductSplitUi = ({
  batchDetails,
  setTemplates,
  handleBatchSplitUpdate,
  selectedIndex,
  setEditedMap,
  setSelectedIndex,
  setFocusedField,
  editedMap,
  focusedField,
  handleAddBatch,
  handleDeleteBatchDialog,
  handleDeleteBatch,
  templates,
  updateBatchSplit,
  globalData,
  middleContentHeight = 0,
}) => {
  const current = batchDetails?.[selectedIndex];
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const getDuplicatedProductRecipeNames = (batchDetails, selectedProductId) => {
    const recipesWithProduct = batchDetails.filter((batch) =>
      batch.products?.some(
        (product) => product.product_id === selectedProductId
      )
    );

    if (recipesWithProduct.length > 1) {
      return recipesWithProduct.map((batch) => batch.recipe_name);
    }

    return []; // No duplicates
  };

  // Add refs for batch inputs
  const batchInputRefs = useRef({});

  const handleScroll = (direction) => {
    const scrollAmount = 200;
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeft(scrollLeft > 0);
      setShowRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    window.addEventListener("resize", checkScroll);
    if (ref) ref.addEventListener("scroll", checkScroll);
    return () => {
      window.removeEventListener("resize", checkScroll);
      if (ref) ref.removeEventListener("scroll", checkScroll);
    };
  }, [current?.products?.[0]?.batches?.length]);

  function getBatchColumnWeight(products, batchIndex) {
    return products.reduce((total, product) => {
      const weight = product.batches?.[batchIndex]?.weight ?? 0;
      return total + weight;
    }, 0);
  }

  // Calculate the maximum number of batches across all products
  const maxBatchCount = Math.max(
    ...(current?.products?.map((product) => product?.batches?.length || 0) || [
      0,
    ])
  );

  return (
    <Stack
      display="flex"
      direction="row"
      height={{ sm: `calc(90vh - 164px - ${middleContentHeight}px)` }}
      flexWrap="wrap"
      sx={{ overflowY: "auto" }}
    >
      <Box
        backgroundColor="background.default"
        sx={{
          flexGrow: 1,
          flexBasis: "256px",
          maxWidth: { sm: "256px", xs: "100%" },
        }}
      >
        <Stack height="34px" display="flex" justifyContent="center" px="8px">
          <Typography fontWeight={600} fontSize="12px">
            Batch Name
          </Typography>
        </Stack>
        <Box
          sx={{
            height: {
              md: `calc(90vh - 198px - ${middleContentHeight}px)`,
              xs: "auto",
            },
            overflow: "auto",
            px: "8px",
          }}
        >
          <List sx={{ border: 1, borderColor: "divider" }} disablePadding>
            {batchDetails?.map((batch, index) => {
              const selected = selectedIndex === index;
              return (
                <ListItemButton
                  key={index}
                  onClick={() => setSelectedIndex(index)}
                  sx={{
                    padding: "8px 12px",
                    color: "text.primary",
                    backgroundColor: "background.paper",
                    "&.Mui-selected": {
                      backgroundColor: "primary.main",
                      color: "#ffffff",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "primary.main",
                    },
                    gap: "12px",
                  }}
                  selected={selected}
                >
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        component="span"
                      >
                        <Typography
                          fontWeight={500}
                          fontSize="14px"
                          alignContent="center"
                          sx={{
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {batch?.recipe_name}
                        </Typography>
                      </Stack>
                    }
                  />
                  <StyledBadge
                    badgeContent={batch?.batch_count}
                    active={selected}
                  />
                  <Stack direction="row" gap="8px" alignItems="center">
                    <img
                      alt="details"
                      src={arrowIcon}
                      style={{
                        height: "10px",
                      }}
                    />
                  </Stack>
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Box>

      {/* Main */}
      <Box p="8px" minWidth="300px" flex={1} overflow="hidden">
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          px="8px"
          height="52px"
        >
          <Stack>
            <Typography fontSize={14} fontWeight={500}>
              {current?.recipe_name}
            </Typography>
            <Typography fontSize={10} fontWeight={500} color="text.secondary">
              Total Weight :
              {formatWeightWithUnits(
                current?.total_weight,
                current?.total_weight_unit,
                globalData?.all_Units
              )}
            </Typography>
          </Stack>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddBatch}
            variant="outlined"
            color="gray2"
          >
            Add Batch
          </Button>
        </Stack>

        {/* Shared Scrollable Area for Header & Rows */}
        <Box sx={{ position: "relative" }}>
          {/* Scroll Arrows */}
          {showLeft && (
            <Box
              height="55px"
              width="30px"
              sx={{
                position: "absolute",
                zIndex: 1,
                backgroundColor: "#DFE8F2",
                boxShadow: 1,
              }}
              onClick={() => handleScroll("left")}
            >
              <IconButton size="small">
                <ChevronLeftIcon fontSize="small" sx={{ mt: 2 }} />
              </IconButton>
            </Box>
          )}

          {/* Scrollable Wrapper */}
          <Box
            ref={scrollRef}
            sx={{
              overflowX: "auto",
              maxHeight: { sm: "calc(90vh - 232px)", xs: "auto" },
              backgroundColor: "background.default",
              px: "8px",
              pb: "8px",
            }}
          >
            {/* Header */}
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                minWidth: "max-content",
                py: "8px",
              }}
              gap="8px"
            >
              <Box width="340px">
                <Typography fontWeight={600} fontSize={"12px"}>
                  Products
                </Typography>
              </Box>
              <Box width="100px">
                <Typography fontWeight={600} fontSize={"12px"}>
                  Quantity
                </Typography>
              </Box>
              {/* Render batch columns based on maxBatchCount */}
              {Array.from({ length: maxBatchCount }).map((_, j) => (
                <Box
                  key={j}
                  display="flex"
                  alignItems="center"
                  flexShrink={0}
                  width={"120px"}
                >
                  <Stack direction="column">
                    <Typography fontWeight={600} fontSize={"12px"}>{`Batch ${
                      j + 1
                    }`}</Typography>
                    <Typography
                      fontSize={12}
                      color={
                        getBatchColumnWeight(current?.products, j) >
                        current?.maxPerBatch
                          ? "error.main"
                          : "text.secondary"
                      }
                    >
                      {getBatchColumnWeight(current?.products, j) === 0
                        ? "0 kg"
                        : formatWeightWithUnits(
                            getBatchColumnWeight(current?.products, j),
                            "kg",
                            globalData?.all_Units
                          )}
                      /{" "}
                      {formatWeightWithUnits(
                        current?.bowl_cappcity,
                        "kg",
                        globalData?.all_Units
                      )}
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    sx={{ color: "#F04438" }}
                    onClick={() => handleDeleteBatchDialog(true, j)}
                    disabled={current?.products[0]?.batches?.length < 2}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {showRight && (
                <Box
                  height="55px"
                  width="30px"
                  sx={{
                    position: "absolute",
                    right: 0,
                    backgroundColor: "#DFE8F2",
                    boxShadow: 1,
                    zIndex: 1,
                  }}
                  onClick={() => handleScroll("right")}
                >
                  <IconButton size="small">
                    <ChevronRightIcon fontSize="small" sx={{ mt: 2 }} />
                  </IconButton>
                </Box>
              )}
            </Stack>

            {/* Body */}
            {current?.products?.map((product, i) => {
              const duplicatedRecipeNames = getDuplicatedProductRecipeNames(
                batchDetails,
                product?.product_id
              );

              return (
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                    p: "8px",
                    minWidth: "max-content",
                    backgroundColor: "background.paper",
                  }}
                >
                  <Stack width="347px" direction={"row"}>
                    <Typography
                      width={"301px"}
                      fontSize={"14px"}
                      fontWeight={400}
                    >
                      {product?.product_name}
                      {duplicatedRecipeNames.length > 0 && (
                        <IconButton
                          size="small"
                          sx={{
                            borderRadius: "100%",
                            width: "26px",
                            height: "26px",
                            bgcolor: "#1760D21F",
                            ml: 1,
                          }}
                        >
                          <Tooltip
                            title={
                              <Box>
                                {duplicatedRecipeNames.map((name, idx) => (
                                  <Typography key={idx} fontSize={13}>
                                    {name}
                                  </Typography>
                                ))}
                              </Box>
                            }
                            arrow
                          >
                            <img
                              src="/icons/ic_batch_box.png"
                              height={"16px"}
                              width={"16px"}
                              alt="Batch Duplicates"
                            />
                          </Tooltip>
                        </IconButton>
                      )}
                    </Typography>
                  </Stack>
                  <Box width="100px">
                    <Typography>{`${product?.quantity} ${product?.unit}`}</Typography>
                  </Box>
                  {/* Render batch inputs for each batch column, or empty if not present */}
                  {Array.from({ length: maxBatchCount }).map((_, j) => {
                    const val = product?.batches?.[j];
                    const fieldKey = `${i}-${j}`;
                    const isEdited = editedMap[fieldKey];
                    const isFocused = focusedField === fieldKey;
                    const isError = val?.hasError;
                    const isLoading =
                      updateBatchSplit?.isPending &&
                      templates?.batchIndex === j &&
                      templates.productIndex === i;
                    // Assign ref for each batch input
                    if (!batchInputRefs.current[`${i}-${j}`]) {
                      batchInputRefs.current[`${i}-${j}`] = React.createRef();
                    }
                    return (
                      <Box
                        key={j}
                        ml={1}
                        display="flex"
                        alignItems="center"
                        width={"120px"}
                      >
                        {val ? (
                          <TextField
                            inputRef={batchInputRefs.current[`${i}-${j}`]}
                            value={val?.units}
                            error={isError}
                            onFocus={() => {
                              setFocusedField(fieldKey);
                              if (val?.units === 0) {
                                setTemplates((draft) => {
                                  draft.batchDetails[selectedIndex].products[
                                    i
                                  ].batches[j].units = "";
                                });
                              }
                            }}
                            onBlur={() => {
                              if (val?.units === "" || val?.units === null) {
                                // If left empty, reset back to 0
                                setTemplates((draft) => {
                                  draft.batchDetails[selectedIndex].products[
                                    i
                                  ].batches[j].units = 0;
                                });
                              }
                              if (isEdited) {
                                handleBatchSplitUpdate(i, j, val.units || 0);
                              }
                              setFocusedField("");
                            }}
                            onChange={(e) => {
                              const newVal = e.target.value;
                              setTemplates((draft) => {
                                draft.batchDetails[selectedIndex].products[
                                  i
                                ].batches[j].units = newVal && Number(newVal);
                                draft.batchDetails[selectedIndex].products[
                                  i
                                ].batches[j].hasError = false;
                              });
                              setEditedMap((prev) => ({
                                ...prev,
                                [fieldKey]: true,
                              }));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === "Tab") {
                                const nextKey = `${i}-${j + 1}`;
                                const nextProductKey = `${i + 1}-0`;
                                if (batchInputRefs.current[nextKey]) {
                                  batchInputRefs.current[
                                    nextKey
                                  ].current?.focus();
                                  setFocusedField(nextKey);
                                  e.preventDefault();
                                } else if (
                                  batchInputRefs.current[nextProductKey]
                                ) {
                                  batchInputRefs.current[
                                    nextProductKey
                                  ].current?.focus();
                                  setFocusedField(nextProductKey);
                                  e.preventDefault();
                                } else {
                                  if (isEdited) {
                                    handleBatchSplitUpdate(
                                      i,
                                      j,
                                      val.units || 0
                                    );
                                    e.preventDefault();
                                  }
                                }
                              }
                            }}
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                              },
                              width: "99px",
                            }}
                            InputProps={{
                              endAdornment: isLoading ? (
                                <InputAdornment position="end">
                                  <Box
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <CircularProgress size={18} />
                                  </Box>
                                </InputAdornment>
                              ) : isError ? (
                                <InputAdornment position="end">
                                  <Box
                                    sx={{
                                      borderRadius: "6px",
                                      width: 24,
                                      height: 24,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <img src={errorIcon} alt="error" />
                                  </Box>
                                </InputAdornment>
                              ) : isFocused && isEdited ? (
                                <InputAdornment position="end">
                                  <Box
                                    onClick={() =>
                                      handleBatchSplitUpdate(i, j, val.units)
                                    }
                                    sx={{
                                      backgroundColor: "#CBEDCC",
                                      borderRadius: "6px",
                                      width: 24,
                                      height: 24,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <img src={checkIcon} alt="check" />
                                  </Box>
                                </InputAdornment>
                              ) : null,
                            }}
                          />
                        ) : (
                          <Box sx={{ width: "99px" }} />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              );
            })}
          </Box>
        </Box>
      </Box>

      <CustomeDialog
        PaperProps={{
          sx: {
            minWidth: "500px",
          },
        }}
        closeIcon={false}
        open={templates?.isDeleteBatchDialogOpen}
        title={
          <Stack
            direction="row"
            alignItems="center"
            gap="8px"
            p="16px"
            pb="8px"
          >
            <img src="/icons/ic_pair_dialog.svg" alt="pair icon" />
            <Typography fontWeight={500} variant="body1" fontSize={20}>
              Remove Batch?
            </Typography>
          </Stack>
        }
        content={
          <DeleteBatchesDialog
            content={
              <Typography>
                Are you sure you want to remove Batch{" "}
                <strong>
                  {templates?.batchIndex && templates?.batchIndex + 1}
                </strong>
                ?
              </Typography>
            }
            footer={
              <CustomDialogFooter
                onSubmit={handleDeleteBatch}
                handleClose={() => handleDeleteBatchDialog(false)}
                submitButton="Delete"
                submitButtonColor="error"
              />
            }
          />
        }
      />

      <CustomeDialog
        PaperProps={{
          sx: {
            minWidth: "500px",
          },
        }}
        closeIcon={false}
        open={templates?.allLastBatchWeightZero}
        title={
          <Stack
            direction="row"
            alignItems="center"
            width="100%"
            p="16px 16px"
            pb="8px"
            gap="8px"
          >
            <img
              src="/icons/ic_pair_dialog.svg"
              alt="remove empty batch icon"
            />
            <Typography fontWeight={500} variant="body1" fontSize={20}>
              Remove Empty Batches?
            </Typography>
          </Stack>
        }
        content={
          <DeleteBatchesDialog
            content={
              <Typography>
                Empty batch(es) will be removed while updating the changes in
                templates.
              </Typography>
            }
            footer={
              <CustomDialogFooter
                handleClose={() => handleDeleteBatchDialog(false)}
                onSubmit={() => handleDeleteBatch("all")}
                submitButton="Ok"
                submitButtonColor="error"
              />
            }
          />
        }
      />
    </Stack>
  );
};

export default ProductSplitUi;
